import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Lock, AlertCircle, TrainFront } from "lucide-react";
import { useDtmAuth } from "../../contexts/DtmAuthContext";

export default function DtmChangePassword() {
  const { changePassword } = useDtmAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPassword.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErr("New password and confirmation do not match.");
      return;
    }
    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success("Password updated. Welcome to your Interview Coach portal.");
    } catch (er) {
      setErr(er?.response?.data?.detail || "Could not update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1B2E] flex items-center justify-center p-4" data-testid="dtm-change-password-page">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 justify-center mb-6">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
            <TrainFront className="w-6 h-6 text-[#0F1B2E]" strokeWidth={2.4} />
          </div>
          <div className="text-white">
            <p className="font-extrabold leading-none">Interview Coach</p>
            <p className="text-[11px] uppercase tracking-widest text-slate-400">Train Driver Foundation</p>
          </div>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl p-7 space-y-4">
          <div className="flex items-center gap-2 text-[#E63B2E]">
            <KeyRound className="w-5 h-5" />
            <h1 className="tdf-heading text-xl text-tdf-navy">Set a new password</h1>
          </div>
          <p className="text-sm text-slate-500">
            For security, you must change your temporary password before accessing your dashboard.
          </p>
          <div>
            <label className="block text-sm font-bold text-tdf-navy mb-1.5">Current (temporary) password</label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password" required value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="dtm-current-password-input"
                className="w-full rounded-lg border border-slate-300 focus:border-[#E63B2E] focus:ring-2 focus:ring-[#E63B2E]/20 outline-none pl-10 pr-4 py-2.5"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-tdf-navy mb-1.5">New password</label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password" required value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="dtm-new-password-input"
                className="w-full rounded-lg border border-slate-300 focus:border-[#E63B2E] focus:ring-2 focus:ring-[#E63B2E]/20 outline-none pl-10 pr-4 py-2.5"
                placeholder="At least 8 characters"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-tdf-navy mb-1.5">Confirm new password</label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password" required value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="dtm-confirm-password-input"
                className="w-full rounded-lg border border-slate-300 focus:border-[#E63B2E] focus:ring-2 focus:ring-[#E63B2E]/20 outline-none pl-10 pr-4 py-2.5"
              />
            </div>
          </div>
          {err && (
            <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3 text-sm" data-testid="dtm-change-password-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
            </div>
          )}
          <button type="submit" disabled={loading} data-testid="dtm-change-password-submit" className="w-full btn-pill btn-primary disabled:opacity-60">
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
